import { CreditCard } from "@/api/credit-cards/credit-cards";
import AmountInputBox from "@/components/ui/amountInputBox";
import { Button } from "@/components/ui/button/button";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import FloatingInput from "@/components/ui/floatingInput";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "@/components/ui/form/form";
import PaymentCard from "@/components/ui/paymentCard";
import SubmitButton from "@/components/ui/submit-button";
import { useValidatePromoCode } from "@/features/transactions/useTransactionPreview";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DepositHeader from "../../_components/DepositHeader";
import { MIN_TRANSFER, MAX_TRANSFER_IN } from "@/constants/common-constants";
import {
	currencyFormatter,
	isValidAmount,
	meetsMaximumTransfer,
	meetsMinimumTransfer,
} from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";

const chargeSchema = z.object({
	amount: z
		.string()
		.min(1, "Amount is required")
		.refine(isValidAmount, {
			message: "Invalid amount",
		})
		.refine(meetsMinimumTransfer, {
			message: `Minimum transaction amount is ${currencyFormatter.format(
				MIN_TRANSFER,
			)}`,
		})
		.refine((val) => meetsMaximumTransfer(val, MAX_TRANSFER_IN), {
			message: `Maximum transaction amount is ${currencyFormatter.format(
				MAX_TRANSFER_IN,
			)}`,
		}),
	promoCode: z.string().trim(),
});

type ChargeFormData = z.infer<typeof chargeSchema>;

interface ChargePageProps {
	promo: string;
	onContinue: (amountDollars: number, promo: string) => void;
	selectedCard: CreditCard;
	defaultAmount: number;
	locationId: number;
	onBack: () => void;
	onClose: () => void;
}

const FORM_ID = "charge-form" as const;

const SelectDepositAmount: React.FC<ChargePageProps> = ({
	onContinue,
	selectedCard,
	defaultAmount,
	locationId,
	onBack,
	onClose,
	promo,
}) => {
	const methods = useForm<ChargeFormData>({
		resolver: zodResolver(chargeSchema),
		mode: "onChange",
		defaultValues: {
			amount: defaultAmount.toString(),
			promoCode: promo,
		},
	});

	const { control, setValue, getValues } = methods;

	const onSubmit = (data: ChargeFormData) => {
		if (data.promoCode) {
			promoValidationMutation.mutate(
				{
					promoCode: data.promoCode,
					requestingAmount: Number(data.amount),
				},
				{
					onSuccess: (response) => {
						if (response.is_valid_promo_code) {
							onContinue(Number(data.amount), data.promoCode);
						}
					},
					onError: (error) => {
						toast({
							title: "Invalid promo code",
							description: error.message,
							variant: "destructive",
						});
					},
				},
			);
		} else {
			onContinue(Number(data.amount), "");
		}
	};

	const onDrawerContinueWithoutPromo = () => {
		setValue("promoCode", "", { shouldDirty: true });

		const amountDollars = getValues("amount");
		onContinue(Number(amountDollars), "");
	};

	const promoValidationMutation = useValidatePromoCode(locationId);

	return (
		<div className="flex h-[100dvh] min-h-[100dvh] w-full flex-col">
			<Form {...methods}>
				<form
					id={FORM_ID}
					onSubmit={methods.handleSubmit(onSubmit)}
					className="flex h-full min-h-0 flex-1 flex-col justify-between kashu-container py-4"
				>
					<div className="flex w-full flex-col gap-4 md:mx-auto md:max-w-lg md:gap-6">
						<DepositHeader
							title="Choose Amount"
							onBack={onBack}
							onClose={onClose}
						/>
						<div>
							<FormField
								control={control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<AmountInputBox
												price={field.value}
												setPrice={field.onChange}
												onFocus={() => {}}
												type="tel"
												inputMode="decimal"
												data-testid="amount-input"
											/>
										</FormControl>
										<FormMessage className="text-center" />
									</FormItem>
								)}
							/>
						</div>
						<p className="text-sm text-center text-textDark md:px-4">
							Platform threshold recommends staying below 60% utilization per
							invoice.
						</p>

						<div className="w-full">
							<PaymentCard card={selectedCard} />
						</div>
					</div>
					<div className="flex w-full shrink-0 flex-col gap-4 pt-6 md:mx-auto md:max-w-lg">
						<FormField
							control={control}
							name="promoCode"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<FloatingInput
											label="Promo Code"
											type="text"
											value={field.value ?? ""}
											onChange={(e) => field.onChange(e.target.value)}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						<p className="text-sm font-medium text-textLight text-center md:px-4">
							<b>Best Practice:</b> To avoid payment delays, consider notifying
							your bank prior to this transaction. Please avoid submitting
							duplicate transactions on the same card within 24 hours.
						</p>
						<SubmitButton
							className="w-full text-white"
							type="submit"
							testId="continue-btn"
							mutationStatus={promoValidationMutation.status}
							loadingText="Validating Promo Code"
						>
							Continue
						</SubmitButton>
					</div>
				</form>
			</Form>
			<Drawer
				open={
					promoValidationMutation.status === "success" &&
					promoValidationMutation.data.is_valid_promo_code === false
				}
				onOpenChange={() => {
					promoValidationMutation.reset();
				}}
			>
				<DrawerContent className="mx-auto max-w-lg p-8 md:rounded-t-3xl">
					<DrawerHeader className="m-4">
						<div className="flex flex-col items-center gap-4 text-center">
							<Info className="w-12 h-12 text-utility-red" />
							<DrawerTitle>Invalid Promo Code</DrawerTitle>
							<DrawerDescription>
								This code may have expired or been entered incorrectly
							</DrawerDescription>
						</div>
					</DrawerHeader>
					<DrawerFooter className="mt-4 gap-6">
						<Button
							className="w-full"
							onClick={() => {
								promoValidationMutation.reset();
							}}
						>
							Try Again
						</Button>
						<Button
							className="w-full text-center"
							variant="unstyled"
							onClick={onDrawerContinueWithoutPromo}
						>
							Continue Without Promo Code
						</Button>
					</DrawerFooter>
				</DrawerContent>
			</Drawer>
		</div>
	);
};

export default SelectDepositAmount;
